import React, { useState, useEffect } from 'react';
import './App.css';
 
const SITE_URL = 'https://root-workout.nl';
const API_URL = `${SITE_URL}/wp-json/wp/v2`;
 
const TOKEN_KEY = 'rw_token';
const EMAIL_KEY = 'rw_email';
 
// Module-taxonomie-ID's (vast, zie project-overview.md).
const BASIS = { id: 24, name: 'Basis' };
const HEALTHY_HABITS_ID = 40;
const MOBILITY_ID = 46;
const UPGRADE_MODULES = [
  { id: 41, name: 'Billen' },
  { id: 42, name: 'Benen' },
  { id: 43, name: 'Strength' },
  { id: 44, name: 'Borst & Armen' },
  { id: 45, name: 'Bovenlichaam' },
];
 
// Week N (1-8) -> week-taxonomie-term-ID (Week N = 24 + N).
const WEEK_TERM = Object.fromEntries([1, 2, 3, 4, 5, 6, 7, 8].map(n => [n, 24 + n]));
const DAY_ORDER = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];
 
// ---------------------------------------------------------------------------
// Auth helpers
// ---------------------------------------------------------------------------
 
async function fetchMyAccess(token) {
  const res = await fetch(`${SITE_URL}/wp-json/rootworkout/v1/my-access`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Sessie verlopen, log opnieuw in.');
  return res.json();
}
 
async function loginWithPassword(username, password) {
  const res = await fetch(`${SITE_URL}/wp-json/jwt-auth/v1/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json();
  if (!res.ok || !data.token) {
    const message = data && data.message ? data.message.replace(/<[^>]+>/g, '') : 'Inloggen mislukt.';
    throw new Error(message);
  }
  return data; // { token, user_email, user_nicename, user_display_name }
}
 
// ---------------------------------------------------------------------------
// Data helpers (programma per module/week, en losse workout-bibliotheken)
// ---------------------------------------------------------------------------
 
async function fetchProgrammaByModuleWeek(moduleId, weekId) {
  const res = await fetch(`${API_URL}/programma?module=${moduleId}&week=${weekId}&per_page=50&_fields=id,title,content,acf`);
  const posts = await res.json();
  const byDay = {};
  (Array.isArray(posts) ? posts : []).forEach(p => {
    const day = p.content.rendered.replace(/<[^>]+>/g, '').trim();
    byDay[day] = { title: p.title.rendered, workoutIds: (p.acf && p.acf.workouts) || [] };
  });
  return byDay;
}
 
async function expandWorkoutIds(ids) {
  if (ids.length === 0) return {};
  const res = await fetch(`${API_URL}/workouts?include=${ids.join(',')}&per_page=100&_fields=id,title,acf`);
  const list = await res.json();
  const map = {};
  (Array.isArray(list) ? list : []).forEach(w => { map[w.id] = w; });
  return map;
}
 
async function fetchModuleWeek(moduleId, weekId) {
  const byDay = await fetchProgrammaByModuleWeek(moduleId, weekId);
  const days = DAY_ORDER.filter(day => byDay[day]).map(day => ({ day, ...byDay[day] }));
  const allIds = Array.from(new Set(days.flatMap(d => d.workoutIds)));
  const workoutMap = await expandWorkoutIds(allIds);
  return days.map(d => ({ ...d, workouts: d.workoutIds.map(id => workoutMap[id]).filter(Boolean) }));
}
 
async function fetchWorkoutLibrary(moduleId) {
  const res = await fetch(`${API_URL}/workouts?module=${moduleId}&per_page=50&_fields=id,title,acf`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}
 
async function fetchCoachingArticles() {
  // acf.video_url is optioneel — nog niet elk artikel heeft een video (zie project-overview.md).
  const res = await fetch(`${API_URL}/coaching?per_page=50&orderby=date&order=desc&_fields=id,title,content,acf`);
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}
 
// ---------------------------------------------------------------------------
// Event-log (per-gebruiker gebruik loggen — basis voor latere analyse:
// populaire workouts, gelezen coaching-artikelen, en op termijn input voor
// AI-op-maat-coaching). Bewust generiek (event_type/object_type/object_id/meta)
// en "fire and forget": een mislukte log-call mag de UI nooit blokkeren of
// een foutmelding tonen.
// ---------------------------------------------------------------------------
 
function logEvent(eventType, objectType, objectId, meta) {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return; // alleen loggen voor ingelogde gebruikers
  fetch(`${SITE_URL}/wp-json/rootworkout/v1/log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ event_type: eventType, object_type: objectType, object_id: objectId, meta }),
  }).catch(() => {});
}
 
// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------
 
function App() {
  const [authState, setAuthState] = useState('checking'); // checking | loggedOut | loggedIn
  const [access, setAccess] = useState(null); // { has_active_subscription, modules, subscriptions }
  const [userEmail, setUserEmail] = useState('');
 
  const [activeScreen, setActiveScreen] = useState('home');
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [returnScreen, setReturnScreen] = useState('home');
 
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) {
      setAuthState('loggedOut');
      return;
    }
    fetchMyAccess(storedToken)
      .then(data => {
        setAccess(data);
        setUserEmail(localStorage.getItem(EMAIL_KEY) || '');
        setAuthState('loggedIn');
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(EMAIL_KEY);
        setAuthState('loggedOut');
      });
  }, []);
 
  function handleLogin(data, accessData) {
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(EMAIL_KEY, data.user_email || '');
    setUserEmail(data.user_email || '');
    setAccess(accessData);
    setAuthState('loggedIn');
    setActiveScreen('home');
  }
 
  function handleLogout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    setAccess(null);
    setUserEmail('');
    setAuthState('loggedOut');
    setActiveScreen('home');
  }
 
  function openWorkout(workout, from) {
    setSelectedWorkout(workout);
    setReturnScreen(from);
    setActiveScreen('detail');
  }
 
  function openModule(module) {
    setSelectedModule(module);
    setActiveScreen('programma');
  }
 
  function openArticle(article) {
    setSelectedArticle(article);
    setActiveScreen('coaching-detail');
  }
 
  if (authState === 'checking') {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.loadingLogo}>ROOT</div>
        <div style={styles.loadingSub}>WORKOUT</div>
      </div>
    );
  }
 
  if (authState === 'loggedOut') {
    return <LoginScreen onLogin={handleLogin} />;
  }
 
  return (
    <div style={styles.app}>
      {activeScreen === 'home' && (
        <HomeScreen access={access} onNavigate={setActiveScreen} onSelectModule={openModule} />
      )}
      {activeScreen === 'programma' && selectedModule && (
        <ProgrammaScreen
          module={selectedModule}
          onNavigate={setActiveScreen}
          onSelectWorkout={w => openWorkout(w, 'programma')}
        />
      )}
      {activeScreen === 'bibliotheek' && (
        <BibliotheekScreen onNavigate={setActiveScreen} onSelectWorkout={w => openWorkout(w, 'bibliotheek')} />
      )}
      {activeScreen === 'detail' && selectedWorkout && (
        <DetailScreen workout={selectedWorkout} backTarget={returnScreen} onNavigate={setActiveScreen} />
      )}
      {activeScreen === 'coaching' && (
        <CoachingScreen onNavigate={setActiveScreen} onSelectArticle={openArticle} />
      )}
      {activeScreen === 'coaching-detail' && selectedArticle && (
        <CoachingDetailScreen article={selectedArticle} onNavigate={setActiveScreen} />
      )}
      {activeScreen === 'profiel' && (
        <ProfielScreen access={access} userEmail={userEmail} onLogout={handleLogout} onNavigate={setActiveScreen} />
      )}
    </div>
  );
}
 
// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------
 
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
 
  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await loginWithPassword(email, password);
      const accessData = await fetchMyAccess(data.token);
      onLogin(data, accessData);
    } catch (err) {
      setError(err.message || 'Inloggen mislukt. Controleer je gegevens.');
    } finally {
      setLoading(false);
    }
  }
 
  return (
    <div style={styles.loginScreen}>
      <div style={styles.loginLogo}>ROOT</div>
      <div style={styles.loginLogoSub}>WORKOUT</div>
 
      <form style={styles.loginForm} onSubmit={handleSubmit}>
        <input
          style={styles.input}
          type="email"
          placeholder="E-mailadres"
          value={email}
          autoComplete="username"
          onChange={e => setEmail(e.target.value)}
          required
        />
        <input
          style={styles.input}
          type="password"
          placeholder="Wachtwoord"
          value={password}
          autoComplete="current-password"
          onChange={e => setPassword(e.target.value)}
          required
        />
        {error && <div style={styles.loginError}>{error}</div>}
        <button style={styles.loginBtn} type="submit" disabled={loading}>
          {loading ? 'Bezig...' : 'Inloggen'}
        </button>
      </form>
 
      <a
        style={styles.loginLink}
        href={`${SITE_URL}/wp-login.php?action=lostpassword`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Wachtwoord vergeten?
      </a>
      <a style={styles.loginLink} href={`${SITE_URL}/mijn-account/`} target="_blank" rel="noopener noreferrer">
        Nog geen account? Meld je aan op root-workout.nl
      </a>
    </div>
  );
}
 
// ---------------------------------------------------------------------------
// Home — "kies je programma"
// ---------------------------------------------------------------------------
 
function HomeScreen({ access, onNavigate, onSelectModule }) {
  const unlocked = (access && access.modules) || [];
  const hasBasis = unlocked.includes(BASIS.id);
  const unlockedExtras = UPGRADE_MODULES.filter(m => unlocked.includes(m.id));
  const lockedExtras = UPGRADE_MODULES.filter(m => !unlocked.includes(m.id));
 
  return (
    <div style={styles.screen}>
      <div style={styles.header}>
        <div>
          <div style={styles.logoText}>ROOT</div>
          <div style={styles.logoSub}>WORKOUT</div>
        </div>
      </div>
 
      <div style={styles.scrollArea}>
        <div style={styles.hero}>
          <div style={styles.greeting}>Welkom terug</div>
          <div style={styles.heroTitle}>Jouw<br />programma's.</div>
        </div>
 
        {!access || !access.has_active_subscription ? (
          <div style={styles.noSubBanner}>
            Geen actief abonnement gevonden op dit account. Net besteld? Probeer over een paar
            minuten opnieuw in te loggen, of check je account op root-workout.nl.
          </div>
        ) : (
          <>
            <div style={styles.sectionLabel}>JOUW PROGRAMMA'S</div>
            {hasBasis && <ModuleCard module={BASIS} onClick={() => onSelectModule(BASIS)} />}
            {unlockedExtras.map(m => (
              <ModuleCard key={m.id} module={m} onClick={() => onSelectModule(m)} />
            ))}
 
            <div style={styles.sectionLabel}>ALTIJD BESCHIKBAAR</div>
            <div style={styles.weekCard} onClick={() => onNavigate('bibliotheek')}>
              <div style={styles.weekCardHeader}>
                <div style={styles.weekBadge}>TOOLKIT</div>
                <div style={styles.weekTitle}>Healthy Habits &amp; Mobility</div>
              </div>
            </div>
          </>
        )}
 
        {lockedExtras.length > 0 && access && access.has_active_subscription && (
          <>
            <div style={styles.sectionLabel}>NOG NIET ACTIEF</div>
            {lockedExtras.map(m => (
              <div key={m.id} style={styles.lockedCard}>
                <div style={styles.weekTitle}>{m.name}</div>
                <div style={styles.oefSets}>Te beheren via je account op root-workout.nl</div>
              </div>
            ))}
          </>
        )}
      </div>
 
      <BottomNav active="home" onNavigate={onNavigate} />
    </div>
  );
}
 
function ModuleCard({ module, onClick }) {
  return (
    <div style={styles.weekCard} onClick={onClick}>
      <div style={styles.weekCardHeader}>
        <div style={styles.weekBadge}>8 WEKEN PROGRAMMA</div>
        <div style={styles.weekTitle}>{module.name}</div>
      </div>
    </div>
  );
}
 
// ---------------------------------------------------------------------------
// Programma (per module: weekkiezer + dagen)
// ---------------------------------------------------------------------------
 
function ProgrammaScreen({ module, onNavigate, onSelectWorkout }) {
  const [weekNum, setWeekNum] = useState(1);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
 
  useEffect(() => {
    logEvent('module_open', 'module', module.id, { week: weekNum });
  }, [module.id, weekNum]);
 
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchModuleWeek(module.id, WEEK_TERM[weekNum])
      .then(result => { if (!cancelled) setDays(result); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [module.id, weekNum]);
 
  return (
    <div style={styles.screen}>
      <div style={styles.header}>
        <span style={styles.backBtn} onClick={() => onNavigate('home')}>‹</span>
        <div style={styles.headerTitle}>{module.name}</div>
      </div>
      <div style={styles.scrollArea}>
        <div style={styles.weekSwitcher}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
            <div
              key={n}
              style={{ ...styles.weekPill, ...(n === weekNum ? styles.weekPillActive : {}) }}
              onClick={() => setWeekNum(n)}
            >
              W{n}
            </div>
          ))}
        </div>
 
        {loading && <div style={styles.testInfo}>Laden...</div>}
        {!loading && days.length === 0 && (
          <div style={styles.testInfo}>Nog geen programma voor deze week.</div>
        )}
 
        {!loading && days.map(day => (
          <div key={day.day} style={styles.weekCard}>
            <div style={styles.weekCardHeader}>
              <div style={styles.weekBadge}>{day.day.toUpperCase()}</div>
              <div style={styles.weekTitle}>{day.title}</div>
            </div>
            <div style={{ padding: '4px 18px 14px' }}>
              {day.workouts.length === 0 && (
                <div style={styles.oefSets}>Geen workouts voor deze dag</div>
              )}
              {day.workouts.map(w => (
                <div
                  key={w.id}
                  style={styles.dayWorkoutRow}
                  onClick={() => onSelectWorkout(w)}
                >
                  <div style={styles.oefName}>{w.title.rendered}</div>
                  {w.acf && w.acf.duur_herhalingen && (
                    <div style={styles.oefSets}>{w.acf.duur_herhalingen}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <BottomNav active="home" onNavigate={onNavigate} />
    </div>
  );
}
 
// ---------------------------------------------------------------------------
// Bibliotheek (Healthy Habits + Mobility — altijd beschikbaar)
// ---------------------------------------------------------------------------
 
function BibliotheekScreen({ onNavigate, onSelectWorkout }) {
  const [healthy, setHealthy] = useState([]);
  const [mobility, setMobility] = useState([]);
  const [loading, setLoading] = useState(true);
 
  useEffect(() => {
    Promise.all([fetchWorkoutLibrary(HEALTHY_HABITS_ID), fetchWorkoutLibrary(MOBILITY_ID)])
      .then(([healthyList, mobilityList]) => {
        setHealthy(healthyList);
        setMobility(mobilityList);
      })
      .finally(() => setLoading(false));
  }, []);
 
  return (
    <div style={styles.screen}>
      <div style={styles.header}>
        <div style={styles.headerTitle}>Bibliotheek</div>
      </div>
      <div style={styles.scrollArea}>
        {loading && <div style={styles.testInfo}>Laden...</div>}
 
        {!loading && (
          <>
            <div style={styles.sectionLabel}>HEALTHY HABITS</div>
            {healthy.map(w => (
              <WorkoutRow key={w.id} workout={w} onClick={() => onSelectWorkout(w)} />
            ))}
 
            <div style={styles.sectionLabel}>MOBILITY</div>
            {mobility.map(w => (
              <WorkoutRow key={w.id} workout={w} onClick={() => onSelectWorkout(w)} />
            ))}
          </>
        )}
      </div>
      <BottomNav active="workout" onNavigate={onNavigate} />
    </div>
  );
}
 
function WorkoutRow({ workout, onClick }) {
  return (
    <div style={styles.oefItem} onClick={onClick}>
      <div style={styles.oefInfo}>
        <div style={styles.oefName}>{workout.title.rendered}</div>
        {workout.acf && workout.acf.duur_herhalingen && (
          <div style={styles.oefSets}>{workout.acf.duur_herhalingen}</div>
        )}
      </div>
      <div style={styles.oefArrow}>›</div>
    </div>
  );
}
 
// ---------------------------------------------------------------------------
// Workout-detail
// ---------------------------------------------------------------------------
 
function DetailScreen({ workout, backTarget, onNavigate }) {
  useEffect(() => {
    logEvent('workout_view', 'workout', workout.id, { title: workout.title.rendered });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen opnieuw loggen als de workout zelf wisselt
  }, [workout.id]);
 
  return (
    <div style={styles.screen}>
      <div style={styles.header}>
        <span style={styles.backBtn} onClick={() => onNavigate(backTarget || 'home')}>‹</span>
        <div style={styles.headerTitle}>{workout.title.rendered}</div>
      </div>
      <div style={styles.scrollArea}>
        {workout.acf.video_url && (
          <div style={styles.videoWrap}>
            <video controls style={styles.video} src={workout.acf.video_url} />
          </div>
        )}
 
        <div style={styles.detailCard}>
          <div style={styles.detailLabel}>DUUR / HERHALINGEN</div>
          <div style={styles.detailValue}>{workout.acf.duur_herhalingen}</div>
        </div>
 
        {workout.acf.regels && workout.acf.regels.length > 0 && (
          <div style={styles.detailCard}>
            <div style={styles.detailLabel}>REGELS</div>
            {workout.acf.regels.map((r, i) => (
              <div key={i} style={styles.regelItem}>· {r.regel}</div>
            ))}
          </div>
        )}
 
        {workout.acf.waarom && (
          <div style={styles.detailCard}>
            <div style={styles.detailLabel}>WAAROM</div>
            <div style={styles.waaromText}>{workout.acf.waarom}</div>
          </div>
        )}
      </div>
      <BottomNav active="home" onNavigate={onNavigate} />
    </div>
  );
}
 
// ---------------------------------------------------------------------------
// Coaching — artikelen (+ evt. video), zelfde opzet als workouts maar met een
// lichtere achtergrond zodat direct duidelijk is dat dit andere content is.
// ---------------------------------------------------------------------------
 
function CoachingScreen({ onNavigate, onSelectArticle }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
 
  useEffect(() => {
    let cancelled = false;
    fetchCoachingArticles()
      .then(list => { if (!cancelled) setArticles(list); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
 
  return (
    <div style={styles.coachScreen}>
      <div style={styles.coachHeader}>
        <div style={styles.headerTitle}>Coaching</div>
      </div>
      <div style={styles.scrollArea}>
        {loading && <div style={styles.testInfo}>Laden...</div>}
        {!loading && articles.length === 0 && (
          <div style={styles.testInfo}>Binnenkort beschikbaar.</div>
        )}
        {!loading && articles.map(article => (
          <div key={article.id} style={styles.coachItem} onClick={() => onSelectArticle(article)}>
            <div style={styles.oefInfo}>
              <div style={styles.oefName}>{article.title.rendered}</div>
              {article.acf && article.acf.video_url && (
                <div style={styles.oefSets}>▶ Met video</div>
              )}
            </div>
            <div style={styles.oefArrow}>›</div>
          </div>
        ))}
      </div>
      <BottomNav active="coaching" onNavigate={onNavigate} />
    </div>
  );
}
 
function CoachingDetailScreen({ article, onNavigate }) {
  const videoUrl = article.acf && article.acf.video_url;
 
  useEffect(() => {
    logEvent('coaching_read', 'coaching', article.id, { title: article.title.rendered, had_video: !!videoUrl });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen opnieuw loggen als het artikel zelf wisselt
  }, [article.id]);
 
  return (
    <div style={styles.coachScreen}>
      <div style={styles.coachHeader}>
        <span style={styles.backBtn} onClick={() => onNavigate('coaching')}>‹</span>
        <div style={styles.headerTitle}>Coaching</div>
      </div>
      <div style={styles.scrollArea}>
        {videoUrl && (
          <div style={styles.videoWrap}>
            <video controls style={styles.video} src={videoUrl} />
          </div>
        )}
        <div style={styles.coachCard}>
          <div style={styles.coachArticleTitle}>{article.title.rendered}</div>
          <div
            className="rw-article"
            style={styles.articleText}
            dangerouslySetInnerHTML={{ __html: article.content.rendered }}
          />
        </div>
      </div>
      <BottomNav active="coaching" onNavigate={onNavigate} />
    </div>
  );
}
 
// ---------------------------------------------------------------------------
// Profiel
// ---------------------------------------------------------------------------
 
function ProfielScreen({ access, userEmail, onLogout, onNavigate }) {
  const subscriptions = (access && access.subscriptions) || [];
 
  return (
    <div style={styles.screen}>
      <div style={styles.header}>
        <div style={styles.headerTitle}>Profiel</div>
      </div>
      <div style={styles.scrollArea}>
        <div style={styles.detailCard}>
          <div style={styles.detailLabel}>INGELOGD ALS</div>
          <div style={styles.detailValue}>{userEmail || '—'}</div>
        </div>
 
        <div style={styles.detailCard}>
          <div style={styles.detailLabel}>ABONNEMENT</div>
          {subscriptions.length === 0 && (
            <div style={styles.oefSets}>Geen actief abonnement gevonden op dit account.</div>
          )}
          {subscriptions.map(s => (
            <div key={s.id} style={styles.oefSets}>Abonnement #{s.id} — {s.status}</div>
          ))}
        </div>
 
        <a
          style={styles.manageLink}
          href={`${SITE_URL}/mijn-account/`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Account &amp; abonnement beheren op root-workout.nl
        </a>
 
        <div style={styles.logoutBtn} onClick={onLogout}>Uitloggen</div>
      </div>
      <BottomNav active="profiel" onNavigate={onNavigate} />
    </div>
  );
}
 
// ---------------------------------------------------------------------------
// Navigatie
// ---------------------------------------------------------------------------
 
function BottomNav({ active, onNavigate }) {
  return (
    <div style={styles.bottomNav}>
      {[
        { id: 'home', label: 'Home', icon: '⌂' },
        { id: 'workout', label: 'Workout', icon: '◎' },
        { id: 'coaching', label: 'Coaching', icon: '▶' },
        { id: 'profiel', label: 'Profiel', icon: '👤' },
      ].map(item => (
        <div
          key={item.id}
          style={{ ...styles.navItem, ...(active === item.id ? styles.navItemActive : {}) }}
          onClick={() => onNavigate(item.id === 'workout' ? 'bibliotheek' : item.id)}
        >
          <span style={styles.navIcon}>{item.icon}</span>
          <span style={styles.navLabel}>{item.label}</span>
        </div>
      ))}
    </div>
  );
}
 
// ---------------------------------------------------------------------------
// Stijlen
// ---------------------------------------------------------------------------
 
const colors = {
  bg: '#1a1c18',
  card: '#222420',
  border: '#3a3c30',
  accent: '#5a6048',
  gold: '#c8b89a',
  muted: '#7a7b6a',
  dim: '#5a5b4a',
  text: '#e8dcc8',
  danger: '#c98a6b',
};
 
// Coaching krijgt bewust een lichtere versie van hetzelfde palet — zelfde opzet
// als de workout-schermen, maar direct herkenbaar als andere content (Ben's verzoek).
const coachColors = {
  bg: '#33362c',
  card: '#404437',
  border: '#565a49',
};
 
const styles = {
  app: { background: colors.bg, minHeight: '100vh', maxWidth: '430px', margin: '0 auto', fontFamily: 'system-ui, sans-serif' },
  screen: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: colors.bg },
  loadingScreen: { background: colors.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  loadingLogo: { color: colors.gold, fontSize: '28px', letterSpacing: '6px', fontWeight: '500' },
  loadingSub: { color: colors.dim, fontSize: '11px', letterSpacing: '5px', marginTop: '4px' },
  header: { background: colors.bg, padding: '48px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `0.5px solid ${colors.border}` },
  logoText: { color: colors.gold, fontSize: '18px', fontWeight: '500', letterSpacing: '3px' },
  logoSub: { color: colors.dim, fontSize: '9px', letterSpacing: '4px' },
  headerTitle: { color: colors.gold, fontSize: '15px', letterSpacing: '1px', textTransform: 'uppercase' },
  backBtn: { color: colors.gold, fontSize: '28px', cursor: 'pointer', lineHeight: 1 },
  scrollArea: { flex: 1, overflowY: 'auto', paddingBottom: '80px' },
  hero: { padding: '24px 20px 20px' },
  greeting: { color: colors.dim, fontSize: '13px', marginBottom: '4px' },
  heroTitle: { color: colors.text, fontSize: '26px', fontWeight: '500', lineHeight: '1.3', marginBottom: '8px' },
  sectionLabel: { color: colors.dim, fontSize: '10px', letterSpacing: '2px', padding: '12px 20px 8px' },
  weekCard: { background: colors.card, margin: '0 16px 14px', borderRadius: '16px', overflow: 'hidden', border: `0.5px solid ${colors.border}`, cursor: 'pointer' },
  weekCardHeader: { background: colors.accent, padding: '16px 18px 12px' },
  weekBadge: { color: colors.gold, fontSize: '10px', letterSpacing: '2px', marginBottom: '6px' },
  weekTitle: { color: colors.text, fontSize: '17px', fontWeight: '500' },
  oefItem: { display: 'flex', alignItems: 'center', padding: '14px 18px', margin: '0 16px 8px', background: colors.card, borderRadius: '14px', border: `0.5px solid ${colors.border}`, cursor: 'pointer' },
  oefInfo: { flex: 1 },
  oefName: { color: colors.gold, fontSize: '14px', fontWeight: '500' },
  oefSets: { color: colors.dim, fontSize: '12px', marginTop: '2px' },
  oefArrow: { color: colors.border, fontSize: '22px' },
  videoWrap: { margin: '0 16px 14px' },
  video: { width: '100%', borderRadius: '14px', background: '#000' },
  detailCard: { background: colors.card, margin: '0 16px 12px', borderRadius: '14px', padding: '16px', border: `0.5px solid ${colors.border}` },
  detailLabel: { color: colors.dim, fontSize: '10px', letterSpacing: '2px', marginBottom: '8px' },
  detailValue: { color: colors.gold, fontSize: '15px' },
  regelItem: { color: colors.muted, fontSize: '13px', lineHeight: '1.8' },
  waaromText: { color: colors.muted, fontSize: '13px', lineHeight: '1.7' },
  bottomNav: { position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '430px', background: colors.bg, borderTop: `0.5px solid ${colors.border}`, display: 'flex', justifyContent: 'space-around', padding: '10px 0 20px' },
  navItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', cursor: 'pointer', padding: '4px 12px' },
  navItemActive: {},
  navIcon: { fontSize: '20px', color: colors.dim },
  navLabel: { fontSize: '10px', color: colors.dim, letterSpacing: '0.5px' },
  testInfo: { padding: '16px 20px', color: colors.muted, fontSize: '13px' },
 
  weekSwitcher: { display: 'flex', gap: '8px', padding: '4px 20px 16px', overflowX: 'auto' },
  weekPill: { flexShrink: 0, padding: '8px 14px', borderRadius: '20px', background: colors.card, border: `0.5px solid ${colors.border}`, color: colors.muted, fontSize: '13px', cursor: 'pointer' },
  weekPillActive: { background: colors.accent, color: colors.gold, borderColor: colors.accent },
  dayWorkoutRow: { padding: '8px 0', borderBottom: `0.5px solid ${colors.border}`, cursor: 'pointer' },
 
  noSubBanner: { margin: '0 16px 16px', padding: '16px', background: colors.card, border: `0.5px solid ${colors.border}`, borderRadius: '14px', color: colors.muted, fontSize: '13px', lineHeight: '1.6' },
  lockedCard: { margin: '0 16px 12px', padding: '14px 18px', background: 'transparent', border: `0.5px dashed ${colors.border}`, borderRadius: '14px', opacity: 0.6 },
 
  manageLink: { display: 'block', margin: '4px 16px 12px', padding: '14px 18px', textAlign: 'center', color: colors.gold, fontSize: '13px', textDecoration: 'none', border: `0.5px solid ${colors.border}`, borderRadius: '14px' },
  logoutBtn: { margin: '4px 16px 12px', padding: '14px 18px', textAlign: 'center', color: colors.danger, fontSize: '13px', cursor: 'pointer', border: `0.5px solid ${colors.border}`, borderRadius: '14px' },
 
  loginScreen: { background: colors.bg, minHeight: '100vh', maxWidth: '430px', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: 'system-ui, sans-serif' },
  loginLogo: { color: colors.gold, fontSize: '30px', letterSpacing: '6px', fontWeight: '500' },
  loginLogoSub: { color: colors.dim, fontSize: '11px', letterSpacing: '5px', marginBottom: '40px' },
  loginForm: { width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' },
  input: { width: '100%', padding: '14px 16px', borderRadius: '12px', background: colors.card, color: colors.text, border: `0.5px solid ${colors.border}`, fontSize: '14px', boxSizing: 'border-box' },
  loginBtn: { width: '100%', padding: '14px 16px', borderRadius: '12px', background: colors.gold, color: colors.bg, border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer', marginTop: '4px' },
  loginError: { color: colors.danger, fontSize: '12px', lineHeight: '1.5' },
  loginLink: { color: colors.muted, fontSize: '12px', textDecoration: 'underline', marginTop: '18px', textAlign: 'center' },
 
  // Coaching — zelfde vormtaal als de rest van de app, alleen lichtere achtergronden.
  coachScreen: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: coachColors.bg },
  coachHeader: { background: coachColors.bg, padding: '48px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `0.5px solid ${coachColors.border}` },
  coachItem: { display: 'flex', alignItems: 'center', padding: '14px 18px', margin: '0 16px 8px', background: coachColors.card, borderRadius: '14px', border: `0.5px solid ${coachColors.border}`, cursor: 'pointer' },
  coachCard: { background: coachColors.card, margin: '0 16px 12px', borderRadius: '14px', padding: '18px', border: `0.5px solid ${coachColors.border}` },
  coachArticleTitle: { color: colors.gold, fontSize: '17px', fontWeight: '500', marginBottom: '10px', lineHeight: '1.35' },
  articleText: { color: colors.text, fontSize: '14px', lineHeight: '1.7' },
};
 
export default App;
 